import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Container, Table, Badge, Modal, Form, Button, Pagination } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { orderAPI, userAPI } from '../utils/api';
import { FaCamera, FaCheck, FaTimes, FaSearch, FaHistory, FaArrowLeft, FaCalendarAlt } from 'react-icons/fa';

const STATUS_MAP = {
  'PREPARE': { label: '요청됨', variant: 'warning', next: 'PROCESS', nextLabel: '승인하기' },
  'PROCESS': { label: '진행중', variant: 'primary', next: 'DONE', nextLabel: '완료처리' },
  'DONE':    { label: '완료됨', variant: 'success', next: null },
  'REJECT':  { label: '거절됨', variant: 'danger', next: null },
  'Cancel':  { label: '취소됨', variant: 'secondary', next: null },
};

const AdminOrders = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);
  
  const queryParams = new URLSearchParams(location.search);
  const initialStoreId = queryParams.get('storeId');
  const initialStoreName = queryParams.get('storeName');
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeStoreFilter, setActiveStoreFilter] = useState(initialStoreId || null);
  const [activeDateFilter, setActiveDateFilter] = useState(null); // 특정 날짜 필터
  const initialTab = queryParams.get('tab') === 'settlements' ? 'SETTLEMENTS' : 'ORDERS';
  const [viewMode, setViewMode] = useState(initialTab); // 'ORDERS' or 'SETTLEMENTS'
  const [currentUser, setCurrentUser] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.get('tab') === 'settlements') {
      setViewMode('SETTLEMENTS');
    } else {
      setViewMode('ORDERS');
    }
  }, [location.search]);
  
  // 페이지네이션 상태
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  
  const [showDoneModal, setShowDoneModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [doneForm, setDoneForm] = useState({ comment: '', images: [] });
  const [submitting, setSubmitting] = useState(false);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const [orderRes, userRes] = await Promise.all([
        orderAPI.getAll({ is_admin: true, limit: 1000 }),
        userAPI.getProfile().catch(() => null)
      ]);
      
      let data = orderRes.data.results || orderRes.data;
      data.sort((a, b) => new Date(b.date) - new Date(a.date));
      setOrders(data);
      
      if (userRes && userRes.data) {
        setCurrentUser(userRes.data);
      }
    } catch (err) {
      console.error('주문 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    setActiveStoreFilter(queryParams.get('storeId'));
  }, [location.search]);

  const handleStatusUpdate = async (orderId, status) => {
    const msg = status === 'REJECT' ? '주문을 거절하시겠습니까? 결제가 자동 취소됩니다.' : '주문을 승인하시겠습니까?';
    if (!window.confirm(msg)) return;
    
    try {
      await orderAPI.patch(orderId, { status });
      alert(`주문이 ${status === 'PROCESS' ? '승인' : '거절'}되었습니다.`);
      fetchOrders();
    } catch (err) {
      alert('상태 변경에 실패했습니다.');
    }
  };

  const handleSettlementUpdate = async (orderId, isSettled) => {
    if (!window.confirm(isSettled ? '정산 완료 처리하시겠습니까?' : '정산 대기 상태로 변경하시겠습니까?')) return;
    try {
      await orderAPI.patch(orderId, { is_settled: isSettled });
      alert('정산 상태가 변경되었습니다.');
      fetchOrders();
    } catch (err) {
      alert('정산 상태 변경에 실패했습니다.');
    }
  };

  const handleBulkSettlement = async (unsettledIds) => {
    if (!window.confirm(`미정산 ${unsettledIds.length}건을 모두 정산 완료 처리하시겠습니까?`)) return;
    try {
      await Promise.all(unsettledIds.map(id => orderAPI.patch(id, { is_settled: true })));
      alert('정산 처리가 완료되었습니다.');
      fetchOrders();
    } catch (e) {
      alert('정산 처리 중 일부 오류가 발생했습니다.');
    }
  };

  const handleBulkSettlementCancel = async (allOrderIds) => {
    if (!window.confirm(`선택한 월의 모든 정산을 취소하시겠습니까?`)) return;
    try {
      await Promise.all(allOrderIds.map(id => orderAPI.patch(id, { is_settled: false })));
      alert('정산 취소가 완료되었습니다.');
      fetchOrders();
    } catch (e) {
      alert('정산 취소 중 오류가 발생했습니다.');
    }
  };

  const groupedSettlements = useMemo(() => {
    if (viewMode !== 'SETTLEMENTS') return [];
    const groups = {};
    
    orders.forEach(o => {
      if (o.status !== 'DONE') return;
      if (activeStoreFilter && String(o.store?.id) !== String(activeStoreFilter)) return;
      
      const matchesSearch = o.store?.name.includes(searchTerm);
      if (searchTerm && !matchesSearch) return;

      const dateObj = new Date(o.date);
      const monthStr = `${dateObj.getFullYear()}년 ${String(dateObj.getMonth() + 1).padStart(2, '0')}월`;
      const storeId = o.store?.id;
      if (!storeId) return;
      
      const key = `${monthStr}-${storeId}`;
      if (!groups[key]) {
        groups[key] = {
          monthStr,
          storeId,
          storeName: o.store?.name,
          commissionRate: o.store?.commission_rate || 0,
          totalOrders: 0,
          totalPrice: 0,
          totalCommission: 0,
          totalSettlement: 0,
          unsettledIds: [],
          allOrderIds: [],
          allOrders: []
        };
      }
      
      groups[key].totalOrders += 1;
      groups[key].totalPrice += o.price;
      
      const commAmount = Math.floor(o.price * ((o.store?.commission_rate || 0) / 100));
      groups[key].totalCommission += commAmount;
      groups[key].totalSettlement += (o.price - commAmount);
      
      groups[key].allOrderIds.push(o.id);
      groups[key].allOrders.push(o);
      if (!o.is_settled) {
        groups[key].unsettledIds.push(o.id);
      }
    });
    
    return Object.values(groups).sort((a, b) => b.monthStr.localeCompare(a.monthStr));
  }, [orders, viewMode, activeStoreFilter, searchTerm]);

  // 필터링된 데이터 계산 (useMemo로 최적화)
  const filteredOrders = useMemo(() => {
    return orders.filter(o => {
      if (viewMode === 'SETTLEMENTS') return false; // 정산 탭에서는 사용안함

      // 1. 사찰 필터
      if (activeStoreFilter && String(o.store?.id) !== String(activeStoreFilter)) return false;
      
      // 2. 날짜 필터 (YYYY-MM-DD 형식 비교)
      if (activeDateFilter) {
        const orderDate = new Date(o.date).toLocaleDateString('en-CA'); // YYYY-MM-DD
        if (orderDate !== activeDateFilter) return false;
      }
      
      // 3. 검색어 필터
      const matchesSearch = 
        o.store?.name.includes(searchTerm) || 
        o.user?.name.includes(searchTerm) ||
        o.receipt_id?.includes(searchTerm);
        
      return matchesSearch;
    });
  }, [orders, activeStoreFilter, activeDateFilter, searchTerm, viewMode]);

  // 페이지네이션 관련 계산
  const totalPages = viewMode === 'SETTLEMENTS' ? Math.ceil(groupedSettlements.length / itemsPerPage) : Math.ceil(filteredOrders.length / itemsPerPage);
  const currentOrders = viewMode === 'SETTLEMENTS' ? groupedSettlements.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage) : filteredOrders.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // 필터가 바뀔 때 페이지를 1로 리셋
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeStoreFilter, activeDateFilter]);

  const clearFilters = () => {
    setActiveStoreFilter(null);
    setActiveDateFilter(null);
    setSearchTerm('');
    navigate('/admin/orders');
  };

  const handleDateClick = (dateStr) => {
    const dateOnly = new Date(dateStr).toLocaleDateString('en-CA');
    setActiveDateFilter(dateOnly);
  };

  const openDoneModal = (order) => {
    setSelectedOrder(order);
    setDoneForm({ comment: '', images: [] });
    setShowDoneModal(true);
  };

  const handleFileChange = (e) => {
    setDoneForm(prev => ({ ...prev, images: [...prev.images, ...Array.from(e.target.files)] }));
  };

  const removeFile = (index) => {
    setDoneForm(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  };

  const handleDoneSubmit = async (e) => {
    e.preventDefault();
    if (doneForm.images.length === 0) return alert('최소 한 장 이상의 사진을 등록해주세요.');
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('comment', doneForm.comment);
      doneForm.images.forEach(img => formData.append('images', img));
      await orderAPI.done(selectedOrder.id, formData);
      alert('주문이 완료 처리되었습니다.');
      setShowDoneModal(false);
      fetchOrders();
    } catch (err) {
      alert('완료 처리 중 오류가 발생했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ao-wrap">
      <div className="ao-hero">
        <div className="ao-hero-bg">☸</div>
        <Container className="ao-hero-inner">
          <div className="ao-tabs">
            <button className="ao-tab" onClick={() => navigate('/admin/temples')}>사찰 관리</button>
            <button className={`ao-tab ${viewMode === 'ORDERS' ? 'active' : ''}`} onClick={() => { navigate('/admin/orders'); setViewMode('ORDERS'); }}>주문 관리</button>
            <button className={`ao-tab ${viewMode === 'SETTLEMENTS' ? 'active' : ''}`} onClick={() => { navigate('/admin/orders?tab=settlements'); setViewMode('SETTLEMENTS'); }}>정산 관리</button>
            {currentUser?.type === 'Admin' && (
              <button className="ao-tab" onClick={() => navigate('/admin/users')}>유저 관리</button>
            )}
          </div>
          <h1 className="ao-title">
            {viewMode === 'SETTLEMENTS' 
              ? (activeStoreFilter ? `[${initialStoreName || '사찰'}] 정산 관리` : '전체 정산 관리') 
              : (activeStoreFilter ? `[${initialStoreName || '사찰'}] 주문 관리` : activeDateFilter ? `[${activeDateFilter}] 주문 내역` : '주문 내역 관리')}
          </h1>
          <p className="ao-sub">
            {viewMode === 'SETTLEMENTS'
              ? '완료된 주문의 정산 내역을 확인하고 처리합니다.'
              : (activeDateFilter ? `${activeDateFilter}에 접수된 주문을 확인 중입니다.` : '최신 주문 순서대로 목록을 확인하고 상태를 업데이트합니다.')}
          </p>
        </Container>
      </div>

      <Container className="ao-body">
        <div className="ao-toolbar">
          <div className="ao-left-actions">
            {(activeStoreFilter || activeDateFilter) && (
              <button className="ao-back-btn" onClick={clearFilters}>
                <FaArrowLeft /> 전체 주문 보기
              </button>
            )}
            <div className="ao-search">
              <FaSearch className="ao-search-ico" />
              <input 
                type="text" 
                placeholder="주문자, 사찰명 검색…" 
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <button className="ao-refresh" onClick={fetchOrders}><FaHistory /> 새로고침</button>
        </div>

        {loading ? (
          <div className="ao-loading">☸ 로딩 중…</div>
        ) : (viewMode === 'ORDERS' && filteredOrders.length === 0) || (viewMode === 'SETTLEMENTS' && groupedSettlements.length === 0) ? (
          <div className="ao-empty">{viewMode === 'SETTLEMENTS' ? '정산 내역이 없습니다.' : '주문 내역이 없습니다.'}</div>
        ) : (
          <>
            <div className="ao-table-wrap">
              <Table className="ao-table">
                {viewMode === 'SETTLEMENTS' ? (
                  <>
                    <thead>
                      <tr>
                        <th>월별</th>
                        <th>사찰명</th>
                        <th>주문건수</th>
                        <th>총 결제금액</th>
                        <th>수수료/총 정산액</th>
                        <th>상태</th>
                        {currentUser?.type === 'Admin' && <th>관리 (최고관리자 전용)</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {currentOrders.map((g, idx) => {
                        const isAllSettled = g.unsettledIds.length === 0;
                        return (
                          <tr key={idx} style={{ cursor: 'pointer' }}>
                            <td className="ao-date" onClick={() => { setSelectedGroup(g); setShowDetailModal(true); }}>
                              <span style={{textDecoration: 'underline', color: '#009590'}}>{g.monthStr}</span>
                            </td>
                            <td onClick={() => { setSelectedGroup(g); setShowDetailModal(true); }}><div className="ao-store">{g.storeName}</div></td>
                            <td onClick={() => { setSelectedGroup(g); setShowDetailModal(true); }}>{g.totalOrders}건</td>
                            <td className="ao-price" onClick={() => { setSelectedGroup(g); setShowDetailModal(true); }}>{g.totalPrice.toLocaleString()}원</td>
                            <td onClick={() => { setSelectedGroup(g); setShowDetailModal(true); }}>
                              <div style={{fontSize:'0.8rem', color:'#888'}}>수수료({g.commissionRate}%): {g.totalCommission.toLocaleString()}원</div>
                              <div style={{fontWeight:'700', color:'#009590'}}>정산액: {g.totalSettlement.toLocaleString()}원</div>
                            </td>
                            <td onClick={() => { setSelectedGroup(g); setShowDetailModal(true); }}>
                              <Badge bg={isAllSettled ? 'success' : 'warning'}>
                                {isAllSettled ? '정산 완료' : `${g.unsettledIds.length}건 정산 대기`}
                              </Badge>
                            </td>
                            {currentUser?.type === 'Admin' && (
                              <td>
                                <div className="ao-actions">
                                  {isAllSettled ? (
                                    <button className="ao-act-btn ao-act-btn--reject" onClick={() => handleBulkSettlementCancel(g.allOrderIds)}>정산 취소</button>
                                  ) : (
                                    <button className="ao-act-btn ao-act-btn--accept" onClick={() => handleBulkSettlement(g.unsettledIds)}>정산 완료처리</button>
                                  )}
                                </div>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </>
                ) : (
                  <>
                    <thead>
                      <tr>
                        <th>주문일 (클릭 시 필터)</th>
                        <th>사찰/공양종류</th>
                        <th>주문자</th>
                        <th>결제금액</th>
                        <th>상태</th>
                        <th>관리</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentOrders.map(o => (
                        <tr key={o.id}>
                          <td onClick={() => handleDateClick(o.date)} style={{ cursor: 'pointer' }} className="ao-td-date">
                            <div className="ao-date-box">
                              <FaCalendarAlt className="me-1 opacity-50" />
                              <div className="ao-date">{new Date(o.date).toLocaleDateString()}</div>
                            </div>
                            <div className="ao-time">{new Date(o.date).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</div>
                          </td>
                          <td>
                            <div className="ao-store">{o.store?.name}</div>
                            <div className="ao-item">{o.pray_type || '일반 공양'}</div>
                          </td>
                          <td>{o.user?.name}</td>
                          <td className="ao-price">{o.price.toLocaleString()}원</td>
                          <td>
                            <Badge bg={STATUS_MAP[o.status]?.variant || 'secondary'}>
                              {STATUS_MAP[o.status]?.label || o.status}
                            </Badge>
                          </td>
                          <td>
                            <div className="ao-actions">
                              {o.status === 'PREPARE' && (
                                <>
                                  <button className="ao-act-btn ao-act-btn--accept" onClick={() => handleStatusUpdate(o.id, 'PROCESS')}>
                                    <FaCheck /> 승인
                                  </button>
                                  <button className="ao-act-btn ao-act-btn--reject" onClick={() => handleStatusUpdate(o.id, 'REJECT')}>
                                    <FaTimes /> 거절
                                  </button>
                                </>
                              )}
                              {o.status === 'PROCESS' && (
                                <button className="ao-act-btn ao-act-btn--done" onClick={() => openDoneModal(o)}>
                                  완료 처리
                                </button>
                              )}
                              {(o.status === 'DONE' || o.status === 'REJECT' || o.status === 'Cancel') && <span className="ao-status-text">처리 완료</span>}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </>
                )}
              </Table>
            </div>

            {/* 페이지네이션 UI */}
            {totalPages > 1 && (
              <div className="ao-pagination-wrap mt-4">
                <Pagination>
                  <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} />
                  {[...Array(totalPages)].map((_, i) => (
                    <Pagination.Item 
                      key={i + 1} 
                      active={i + 1 === currentPage}
                      onClick={() => setCurrentPage(i + 1)}
                    >
                      {i + 1}
                    </Pagination.Item>
                  ))}
                  <Pagination.Next onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} />
                </Pagination>
              </div>
            )}
          </>
        )}
      </Container>

      {/* 완료 처리 모달 (기존과 동일) */}
      <Modal show={showDoneModal} onHide={() => !submitting && setShowDoneModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>주문 완료 처리</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleDoneSubmit}>
            <div className="ao-modal-info mb-4">
              <strong>주문 정보:</strong> {selectedOrder?.store?.name} - {selectedOrder?.user?.name}님
            </div>
            <Form.Group className="mb-4">
              <Form.Label className="fw-bold">공양 회향 사진 (필수)</Form.Label>
              <div className="ao-upload-zone" onClick={() => fileInputRef.current.click()}>
                <FaCamera size={24} className="mb-2" />
                <div>사진 업로드 (여러 장 가능)</div>
                <input type="file" multiple accept="image/*" hidden ref={fileInputRef} onChange={handleFileChange} />
              </div>
              {doneForm.images.length > 0 && (
                <div className="ao-file-list mt-3">
                  {doneForm.images.map((file, i) => (
                    <div key={i} className="ao-file-item">
                      <span>{file.name}</span>
                      <button type="button" onClick={() => removeFile(i)}>✕</button>
                    </div>
                  ))}
                </div>
              )}
            </Form.Group>
            <Form.Group className="mb-4">
              <Form.Label className="fw-bold">관리자 코멘트 (선택)</Form.Label>
              <Form.Control as="textarea" rows={3} placeholder="보살님께 전달할 메시지를 입력해주세요." value={doneForm.comment} onChange={e => setDoneForm(prev => ({ ...prev, comment: e.target.value }))} />
            </Form.Group>
            <div className="d-flex gap-2">
              <Button variant="secondary" className="flex-grow-1" onClick={() => setShowDoneModal(false)}>취소</Button>
              <Button variant="primary" type="submit" className="flex-grow-1" disabled={submitting}>{submitting ? '처리 중…' : '완료 및 알림 발송'}</Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* 정산 상세 내역 모달 */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} centered size="xl">
        <Modal.Header closeButton>
          <Modal.Title>{selectedGroup?.storeName} - {selectedGroup?.monthStr} 정산 상세 내역</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {selectedGroup && selectedGroup.allOrders.length > 0 ? (
            <Table className="ao-table" bordered hover>
              <thead>
                <tr style={{ backgroundColor: '#f8fbfb' }}>
                  <th>주문일시</th>
                  <th>공양종류</th>
                  <th>주문자</th>
                  <th>결제금액</th>
                  <th>수수료/정산액</th>
                  <th>상태</th>
                </tr>
              </thead>
              <tbody>
                {selectedGroup.allOrders.map(o => {
                  const comm = Math.floor(o.price * ((o.store?.commission_rate || 0) / 100));
                  const settle = o.price - comm;
                  return (
                    <tr key={o.id}>
                      <td>
                        <div className="ao-date">{new Date(o.date).toLocaleDateString()}</div>
                        <div className="ao-time" style={{ paddingLeft: 0 }}>{new Date(o.date).toLocaleTimeString()}</div>
                      </td>
                      <td>{o.pray_type || '일반 공양'}</td>
                      <td>{o.user?.name}</td>
                      <td className="ao-price">{o.price.toLocaleString()}원</td>
                      <td>
                        <div style={{fontSize:'0.8rem', color:'#888'}}>{comm.toLocaleString()}원 ({o.store?.commission_rate || 0}%)</div>
                        <div style={{fontWeight:'700', color:'#009590'}}>{settle.toLocaleString()}원</div>
                      </td>
                      <td>
                        <Badge bg={o.is_settled ? 'success' : 'warning'}>
                          {o.is_settled ? '정산 완료' : '정산 대기'}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          ) : (
            <div className="text-center p-4">상세 내역이 없습니다.</div>
          )}
        </Modal.Body>
      </Modal>

      <style jsx="true">{`
        .ao-wrap { min-height: 100vh; background: #f8fbfb; padding-bottom: 100px; }
        .ao-hero { background: linear-gradient(135deg, #e5f4f4 0%, #d5efef 100%); padding: 50px 0 40px; position: relative; overflow: hidden; border-bottom: none; }
        .ao-hero-bg { position: absolute; right: 10%; top: -20%; font-size: 20rem; color: #fff; z-index: 0; pointer-events: none; opacity: 0.3; transform: rotate(15deg); }
        .ao-hero-inner { position: relative; z-index: 1; }
        .ao-tabs { display: flex; gap: 4px; margin-bottom: 24px; }
        .ao-tab { border: none; background: rgba(0,0,0,0.05); padding: 8px 20px; border-radius: 4px; font-size: 0.88rem; color: #6e8a84; font-weight: 600; cursor: pointer; transition: all 0.2s; }
        .ao-tab.active { background: #009590; color: #fff; }
        .ao-title { font-size: 2.2rem; font-weight: 800; color: #009590; margin-bottom: 12px; letter-spacing: -0.02em; }
        .ao-sub { color: #5a7570; font-size: 1.05rem; margin: 0; }
        .ao-body { padding-top: 30px; }
        .ao-toolbar { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; gap: 12px; }
        .ao-left-actions { display: flex; align-items: center; gap: 12px; flex: 1; }
        .ao-back-btn { background: #fff; border: 1px solid #009590; color: #009590; padding: 8px 16px; border-radius: 4px; font-size: 0.85rem; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 6px; }
        .ao-search { position: relative; flex: 1; max-width: 300px; }
        .ao-search-ico { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #009590; }
        .ao-search input { width: 100%; padding: 10px 12px 10px 38px; border: 1px solid #e4edec; border-radius: 4px; font-size: 0.9rem; outline: none; }
        .ao-refresh { background: none; border: 1px solid #e4edec; padding: 8px 16px; border-radius: 4px; font-size: 0.85rem; color: #4a6660; cursor: pointer; display: flex; align-items: center; gap: 6px; }
        .ao-table-wrap { background: #fff; border: 1px solid #e4edec; border-radius: 8px; overflow: hidden; }
        .ao-table { margin: 0; }
        .ao-table th { background: #fcfdfd; padding: 15px; font-size: 0.8rem; color: #6e8a84; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid #e4edec; }
        .ao-table td { padding: 15px; vertical-align: middle; font-size: 0.9rem; border-bottom: 1px solid #f0f5f5; }
        .ao-td-date:hover { background: #f0fafa !important; }
        .ao-date-box { display: flex; align-items: center; }
        .ao-date { font-weight: 600; color: #009590; }
        .ao-time { font-size: 0.75rem; color: #b2cece; padding-left: 20px; }
        .ao-store { font-weight: 700; color: #2c3e35; }
        .ao-item { font-size: 0.82rem; color: #6e8a84; }
        .ao-price { font-weight: 600; color: #2c3e35; }
        .ao-pagination-wrap { display: flex; justify-content: center; }
        .page-link { color: #009590; }
        .page-item.active .page-link { background-color: #009590; border-color: #009590; }
        
        /* 나머지 스타일 기존과 동일 */
        .ao-actions { display: flex; gap: 6px; }
        .ao-act-btn { padding: 6px 12px; border-radius: 4px; font-size: 0.8rem; font-weight: 700; border: none; cursor: pointer; transition: all 0.2s; }
        .ao-act-btn--accept { background: #009590; color: #fff; }
        .ao-act-btn--reject { background: #fff1f0; color: #ff4d4f; }
        .ao-act-btn--done { background: #1890ff; color: #fff; }
        .ao-status-text { font-size: 0.85rem; font-weight: 600; color: #b2cece; }
        .ao-upload-zone { border: 2px dashed #e4edec; border-radius: 8px; padding: 30px; text-align: center; cursor: pointer; transition: all 0.2s; color: #6e8a84; }
        .ao-upload-zone:hover { background: #f0fafa; border-color: #009590; color: #009590; }
        .ao-file-list { display: flex; flex-direction: column; gap: 4px; }
        .ao-file-item { display: flex; justify-content: space-between; background: #f8fbfb; padding: 6px 12px; border-radius: 4px; font-size: 0.85rem; border: 1px solid #e4edec; }
        .ao-file-item button { border: none; background: none; color: #ff4d4f; font-weight: 700; }
        .ao-loading, .ao-empty { text-align: center; padding: 100px 0; color: #6e8a84; }
      `}</style>
    </div>
  );
};

export default AdminOrders;
